import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import Tabs from 'src/components/Tabs'
import HeaderLoggedIn, {
  HeaderGroup
} from 'src/components/header/HeaderLoggedIn'
import Alert from 'src/components/Alert'
import { Page } from 'src/components/Wrappers'
import StockHeader from 'src/features/stocks/components/StockHeader'
import { breakpoint } from 'src/theme/grid'
import DesktopNotificationService from 'src/services/DesktopNotificationService'

import { connectNotificationsBasics } from 'src/features/notifications/duck/connectors'
import { NotificationsBox } from './components/atoms'
import Section from './components/Section'
import NotificationSetting from './components/NotificationSetting'

const Settings = ({
  className,
  getNotificationsSettings,
  notificationsSettings,
  updateNotificationSetting
}) => {
  const intl = useIntl()
  const desktopNotificationService = DesktopNotificationService.getInstance()
  const [notificationPermission, setNotificationPermission] = useState(() =>
    desktopNotificationService.getPermissionStatus()
  )

  useEffect(() => {
    getNotificationsSettings()

    const checkPermission = () => {
      const currentPermission = desktopNotificationService.getPermissionStatus()
      if (currentPermission !== notificationPermission) {
        setNotificationPermission(currentPermission)
      }
    }

    // Sprawdzaj przy powrocie do karty
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkPermission()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    const interval = setInterval(checkPermission, 2000)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(interval)
    }
  }, [notificationPermission])

  const handleNotificationPermission = async () => {
    try {
      if (notificationPermission === 'granted') {
        // Informuj użytkownika jak wyłączyć powiadomienia
        alert(
          intl.formatMessage({
            id: 'settings.desktopNotificationsDisableInstructions'
          })
        )
      } else if (notificationPermission === 'denied') {
        // Informuj jak odblokować powiadomienia
        alert(
          intl.formatMessage({
            id: 'settings.desktopNotificationsEnableInstructions'
          })
        )
      } else {
        // Włącz powiadomienia
        const permission = await desktopNotificationService.requestPermission()
        setNotificationPermission(permission)
      }
    } catch (e) {
      console.error('Błąd podczas proszenia o uprawnienia do powiadomień:', e)
    }
  }

  const getButtonText = () => {
    switch (notificationPermission) {
      case 'granted':
        return intl.formatMessage({
          id: 'settings.desktopNotificationsDisable'
        })
      case 'denied':
        return intl.formatMessage({ id: 'settings.desktopNotificationsBlock' })
      default:
        return intl.formatMessage({ id: 'settings.desktopNotificationsEnable' })
    }
  }

  const getDescriptionText = () => {
    switch (notificationPermission) {
      case 'granted':
        return intl.formatMessage({ id: 'settings.desktopNotificationsOnText' })
      case 'denied':
        return intl.formatMessage({
          id: 'settings.desktopNotificationsBlockText'
        })
      default:
        return intl.formatMessage({
          id: 'settings.desktopNotificationsOffText'
        })
    }
  }

  return (
    <Page
      className={className}
      contentPadding={0}
      topBar={
        <HeaderLoggedIn>
          <HeaderGroup>
            <StockHeader>
              {intl.formatMessage({
                id: 'settings.pageTitle'
              })}
            </StockHeader>
          </HeaderGroup>
        </HeaderLoggedIn>
      }
    >
      <Tabs list={[intl.formatMessage({ id: 'settings.notifications' })]}>
        <TabContent>
          <Alert
            fullWidth
            type='info'
            message={intl.formatMessage({
              id: 'settings.alertInformationText'
            })}
          />
          <NotificationsBox>
            {notificationsSettings.map(setting => (
              <Section label={setting.label} icon={setting.type}>
                <LabelGroup>
                  <Label>WWW</Label>
                  <Label>E-mail</Label>
                  <Label>Mobile</Label>
                </LabelGroup>
                {setting.values.map(value => (
                  <NotificationSetting
                    settings={value}
                    updateNotificationSetting={updateNotificationSetting}
                  />
                ))}
              </Section>
            ))}
          </NotificationsBox>

          <DesktopNotificationSection>
            <DesktopNotificationTitle>
              {intl.formatMessage({ id: 'settings.desktopNotifications' })}
            </DesktopNotificationTitle>
            <DesktopNotificationDescription>
              {getDescriptionText()}
            </DesktopNotificationDescription>
            {desktopNotificationService.isSupported() ? (
              <DesktopNotificationButton
                onClick={handleNotificationPermission}
                disabled={false}
                permissionState={notificationPermission}
              >
                {getButtonText()}
              </DesktopNotificationButton>
            ) : (
              <Alert type='warning'>
                {intl.formatMessage({
                  id: 'settings.desktopNotificationsNotSupported'
                })}
              </Alert>
            )}
          </DesktopNotificationSection>
        </TabContent>
      </Tabs>
    </Page>
  )
}

const ConnectedComponent = connectNotificationsBasics(Settings)
export default styled(ConnectedComponent)`
  ${Tabs} {
    & > div {
      padding: 0.8rem 1.2rem 0 1.2rem;
      background: transparent;
    }
    & span {
      font-weight: 400;
    }
  }
`

const TabContent = styled.div`
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  padding: 0 0.8rem;
  ${breakpoint.m`
    padding: 0 1.6rem;
  `}
`

const LabelGroup = styled.div`
  display: none;
  width: 180px;
  position: absolute;
  right: 3.2rem;
  top: 0;
  ${breakpoint.m`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `}
`

const Label = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 600;
  width: 40px;
  text-align: center;
`

const DesktopNotificationSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 1.5rem 1.2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
`

const DesktopNotificationTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black.hex()};
  margin: 0 0 0.5rem 0;
`

const DesktopNotificationDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.black.alpha(0.7).hex()};
  margin: 0 0 1rem 0;
  line-height: 1.4;
`

const DesktopNotificationButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  ${({ permissionState, theme }) => {
    switch (permissionState) {
      case 'granted':
        return `
          background-color: ${theme.colors.fadedRed.hex()};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.fadedRed.darken(0.1).hex()};
          }
        `
      case 'denied':
        return `
          background-color: ${theme.colors.corduroy.hex()};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.corduroy.darken(0.1).hex()};
          }
        `
      default:
        return `
          background-color: ${theme.colors.apple.hex()};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.apple.darken(0.1).hex()};
          }
        `
    }
  }}
`
